'use client';

import Image, { type ImageProps } from 'next/image';
import { mediaSrc, type MediaSrcOpts } from '@/contexts/media/utils/mediaSrc';

/**
 * MediaImage — the ONE way to render a stored media value with next/image.
 *
 * The DB stores object KEYS (private R2). A raw key handed to <Image src> is
 * treated as a relative path and 404s (e.g. `/ru/book-images/…/cover.png`).
 * MediaImage always runs `src` through mediaSrc() → the same-origin proxy that
 * 302-redirects to a fresh presigned URL (auth + IDOR enforced, expiry self-
 * heals), and passes public/CDN/data URLs through untouched. Use this instead of
 * `<Image src={mediaSrc(x)}>` everywhere so the wrapping can't be forgotten.
 *
 * `src` is the STORED ref (key or URL). Every other next/image prop (alt, fill,
 * sizes, width/height, className, …) passes straight through — styling is
 * unchanged. Renders nothing when there is no ref (caller supplies its own
 * placeholder, as before).
 */
type MediaImageProps = Omit<ImageProps, 'src'> &
  Pick<MediaSrcOpts, 'admin' | 'download' | 'filename'> & {
    src?: string | null;
  };

export function MediaImage({ src, admin, download, filename, ...rest }: MediaImageProps) {
  const resolved = mediaSrc(src, { admin, download, filename });
  if (!resolved) return null;
  return <Image src={resolved} {...rest} />;
}
